import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Headphones, Music2, Music4, Camera, Video, Sparkles, Gem, UtensilsCrossed, Play, Phone, Mail, Instagram, Globe, ChevronDown, Loader2, Star, Download, Heart, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Icon mapping
const ICONS = {
  headphones: Headphones,
  music2: Music2,
  music4: Music4,
  camera: Camera,
  video: Video,
  sparkles: Sparkles,
  gem: Gem,
  utensils: UtensilsCrossed,
  heart: Heart,
  star: Star,
  phone: Phone,
};

const DossierPublicView = ({ token }) => {
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    loadDossier();
    loadPhotos();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: loadDossier
  }, [token]);

  const loadDossier = async () => {
    try {
      const response = await axios.get(`${API}/api/dossier/preview/${token}`);
      setDossier(response.data.dossier);
    } catch (error) {
      console.error('Error loading dossier:', error);
      setError('Dossier no encontrado o ha expirado');
    } finally {
      setLoading(false);
    }
  };

  const loadPhotos = async () => {
    try {
      const response = await axios.get(`${API}/api/dossier/photos`);
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error loading photos:', error);
    }
  };

  const getVideoEmbedUrl = (url) => {
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    } else if (url.includes('youtube.com/shorts/')) {
      const videoId = url.split('shorts/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      const videoId = urlParams.get('v');
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    } else if (url.includes('vimeo.com/')) {
      const vimeoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : null;
    }
    return null;
  };

  const getVideoThumbnail = (url) => {
    if (url.includes('youtu.be/') || url.includes('youtube.com')) {
      let videoId = '';
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0];
      } else if (url.includes('youtube.com/shorts/')) {
        videoId = url.split('shorts/')[1]?.split('?')[0];
      } else if (url.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        videoId = urlParams.get('v');
      }
      return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
    }
    return null;
  };

  const getIcon = (iconName) => ICONS[iconName] || Star;

  // Get photos for a specific service
  const getServicePhotos = (serviceId) => {
    const selectedPhotoIds = dossier?.selected_photos || [];
    const categoryMap = {
      'coctel': ['coctel'],
      'dj': ['dj'],
      '360': ['360'],
      'fotomaton': ['fotomaton'],
      'audiolibro': ['audiolibro'],
      'baile': ['baile'],
      'saxo': ['saxo'],
      'violin': ['violin'],
      'extras': ['extras'],
      'boda_completa': ['boda_completa', 'coctel', 'dj', 'baile'],
    };
    
    const categories = categoryMap[serviceId] || [];
    let servicePhotos = photos.filter(p => categories.includes(p.category));
    
    // If specific photos were selected, filter by those
    if (selectedPhotoIds.length > 0) {
      servicePhotos = servicePhotos.filter(p => selectedPhotoIds.includes(p.id));
    }
    
    return servicePhotos;
  };

  // Compute all videos to show, avoiding duplicates, using useMemo for consistency
  const { serviceVideosMap, additionalVideos } = useMemo(() => {
    if (!dossier) return { serviceVideosMap: {}, additionalVideos: [] };
    
    const allVideos = dossier?.config?.videos || [];
    const selectedVideoIds = dossier?.selected_videos || [];
    const selectedServices = dossier?.selected_services || [];
    
    // Track which videos have been assigned to avoid duplicates
    const assignedVideoIds = new Set();
    const serviceVideosMap = {};
    
    // First pass: assign videos to their primary service
    selectedServices.forEach(service => {
      const serviceVideoIds = service.videos || [];
      const videosForService = [];
      
      serviceVideoIds.forEach(videoId => {
        // Skip if already assigned to another service
        if (assignedVideoIds.has(videoId)) return;
        
        // Find the video
        const video = allVideos.find(v => v.id === videoId);
        if (!video) return;
        
        // If we have selected_videos, check if this video is selected
        if (selectedVideoIds.length > 0 && !selectedVideoIds.includes(videoId)) return;
        
        videosForService.push(video);
        assignedVideoIds.add(videoId);
      });
      
      serviceVideosMap[service.id] = videosForService;
    });
    
    // Second pass: collect additional/custom videos (selected but not yet assigned)
    const additionalVideos = [];
    
    if (selectedVideoIds.length > 0) {
      // Show all selected videos that haven't been assigned to a service
      selectedVideoIds.forEach(videoId => {
        if (assignedVideoIds.has(videoId)) return;
        
        const video = allVideos.find(v => v.id === videoId);
        if (video) {
          additionalVideos.push(video);
          assignedVideoIds.add(videoId);
        }
      });
    }
    
    return { serviceVideosMap, additionalVideos };
  }, [dossier]);
  
  // Helper function to get videos for a specific service
  const getServiceVideos = (service) => {
    return serviceVideosMap[service.id] || [];
  };
  
  // Helper function to get additional videos
  const getCustomVideos = () => {
    return additionalVideos;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-slate-400">Cargando portfolio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <a href="/" className="text-amber-400 hover:underline">Volver al inicio</a>
        </div>
      </div>
    );
  }

  const config = dossier?.config || {};
  const selectedServices = dossier?.selected_services || [];

  return (
    <div className="min-h-screen bg-[#0f1419]">
      {/* Video Modal */}
      {activeVideo && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setActiveVideo(null)}
        >
          <div className="relative w-full max-w-5xl aspect-video">
            <button 
              onClick={() => setActiveVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-amber-400"
            >
              <X className="w-8 h-8" />
            </button>
            <iframe
              src={getVideoEmbedUrl(activeVideo)}
              className="w-full h-full rounded-xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button 
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 text-white hover:text-amber-400"
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={`${API}${selectedPhoto.url}`} 
            alt={selectedPhoto.title}
            className="max-w-full max-h-[90vh] object-contain rounded-xl"
          />
        </div>
      )}

      {/* Hero Section - Premium Design */}
      <header className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-900/30 via-[#0f1419] to-[#0f1419]" />
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto py-20">
          {/* Brand Logo */}
          <div className="mb-12">
            <div className="inline-flex items-center justify-center w-28 h-28 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-orange-600 mb-8 shadow-2xl shadow-amber-500/40">
              <Music2 className="w-14 h-14 text-white" />
            </div>
          </div>

          {/* Main Title */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black mb-6 tracking-tight">
            <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-orange-400 bg-clip-text text-transparent">
              {config.business_name || 'Luis Herrera'}
            </span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-xl sm:text-2xl md:text-3xl text-amber-300/90 font-light mb-8 tracking-wide max-w-3xl mx-auto">
            Servicio integral para bodas
          </p>

          <p className="text-lg text-slate-400 mb-12 max-w-2xl mx-auto">
            Acompañamos cada momento del día "B" con la mejor música
          </p>

          {/* Client personalization */}
          {dossier?.client_name && (
            <div className="inline-block bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 rounded-2xl px-10 py-5 mb-10 backdrop-blur-sm">
              <span className="text-amber-100 text-lg">
                Preparado especialmente para
              </span>
              <p className="text-3xl font-bold text-white mt-1">{dossier.client_name}</p>
            </div>
          )}

          {/* Custom message */}
          {dossier?.custom_message && (
            <p className="text-slate-300 max-w-2xl mx-auto mb-12 text-lg italic leading-relaxed">
              "{dossier.custom_message}"
            </p>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-16">
            <a
              href={`https://wa.me/${config.whatsapp || '34667472074'}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 px-10 py-5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-2xl font-bold text-lg transition-all transform hover:scale-105 shadow-xl shadow-green-500/30"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Contactar por WhatsApp
            </a>
            <a
              href={`tel:${config.phone || '+34667472074'}`}
              className="flex items-center justify-center gap-3 px-10 py-5 bg-slate-800/80 hover:bg-slate-700 text-white rounded-2xl font-bold text-lg transition-all backdrop-blur-sm border border-slate-600"
            >
              <Phone className="h-6 w-6" />
              Llamar ahora
            </a>
          </div>

          {/* Website link */}
          {config.website && (
            <a 
              href={config.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors"
            >
              <Globe className="w-5 h-5" />
              <span className="font-medium">{config.website.replace('https://', '').replace('http://', '')}</span>
            </a>
          )}
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-10 h-10 text-amber-400/60" />
        </div>
      </header>

      {/* Services Section */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          {selectedServices.map((service, index) => {
            const Icon = getIcon(service.icon);
            const serviceVideos = getServiceVideos(service);
            const servicePhotos = getServicePhotos(service.id);
            const isEven = index % 2 === 0;
            
            return (
              <div 
                key={service.id}
                className="mb-32 last:mb-0"
              >
                {/* Service Header */}
                <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center mb-12`}>
                  {/* Text Content */}
                  <div className="flex-1 text-center lg:text-left">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 mb-6 shadow-lg shadow-amber-500/30`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <h2 className="text-4xl sm:text-5xl font-black text-white mb-3">
                      {service.name}
                    </h2>
                    
                    {service.subtitle && (
                      <p className="text-xl text-amber-400 font-medium mb-6 italic">
                        {service.subtitle}
                      </p>
                    )}
                    
                    <p className="text-lg text-slate-300 leading-relaxed max-w-2xl">
                      {service.description}
                    </p>
                  </div>

                  {/* Main Photo */}
                  {servicePhotos.length > 0 && (
                    <div className="flex-1 max-w-xl">
                      <div 
                        className="relative aspect-[4/3] rounded-3xl overflow-hidden cursor-pointer group shadow-2xl"
                        onClick={() => setSelectedPhoto(servicePhotos[0])}
                      >
                        <img 
                          src={`${API}${servicePhotos[0].url}`}
                          alt={servicePhotos[0].title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Photos Grid */}
                {servicePhotos.length > 1 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-8">
                    {servicePhotos.slice(1, 5).map(photo => (
                      <div
                        key={photo.id}
                        className="relative aspect-square rounded-2xl overflow-hidden cursor-pointer group"
                        onClick={() => setSelectedPhoto(photo)}
                      >
                        <img 
                          src={`${API}${photo.url}`}
                          alt={photo.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors" />
                      </div>
                    ))}
                  </div>
                )}

                {/* Videos Grid */}
                {serviceVideos.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-sm font-bold text-amber-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <Play className="w-4 h-4" />
                      Videos de ejemplo
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {serviceVideos.slice(0, 4).map(video => {
                        const thumbnail = getVideoThumbnail(video.url);
                        return (
                          <button
                            key={video.id}
                            onClick={() => setActiveVideo(video.url)}
                            className="relative aspect-video rounded-xl overflow-hidden group"
                          >
                            {thumbnail ? (
                              <img 
                                src={thumbnail} 
                                alt={video.title}
                                className="w-full h-full object-cover transition-transform group-hover:scale-110"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                                <Play className="w-10 h-10 text-slate-500" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              <div className="w-14 h-14 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg">
                                <Play className="w-6 h-6 text-white ml-1" />
                              </div>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80">
                              <p className="text-sm text-white font-medium truncate">{video.title}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Custom Videos Section - Videos added by user that are not part of services */}
      {getCustomVideos().length > 0 && (
        <section className="py-16 px-4 bg-slate-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">
              <span className="bg-gradient-to-r from-amber-200 to-orange-400 bg-clip-text text-transparent">
                Videos adicionales
              </span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {getCustomVideos().map(video => {
                const thumbnail = getVideoThumbnail(video.url);
                return (
                  <button
                    key={video.id}
                    onClick={() => setActiveVideo(video.url)}
                    className="relative aspect-video rounded-xl overflow-hidden group"
                  >
                    {thumbnail ? (
                      <img 
                        src={thumbnail} 
                        alt={video.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                        <Play className="w-10 h-10 text-slate-500" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg">
                        <Play className="w-6 h-6 text-white ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80">
                      <p className="text-sm text-white font-medium truncate">{video.title}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="py-24 px-4 bg-gradient-to-t from-amber-900/20 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-black mb-6">
            <span className="bg-gradient-to-r from-amber-200 to-orange-400 bg-clip-text text-transparent">
              ¿Dudas?
            </span>
          </h2>
          <p className="text-xl text-amber-300/80 mb-4">Tú preguntas y nosotros respondemos</p>
          <p className="text-slate-400 mb-12 text-lg max-w-2xl mx-auto">
            Cuéntame sobre tu evento y te prepararé una propuesta personalizada sin compromiso
          </p>

          {/* Contact Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            <a
              href={`tel:${config.phone || '+34667472074'}`}
              className="flex flex-col items-center gap-4 p-8 bg-slate-800/50 hover:bg-slate-800 rounded-3xl border border-slate-700/50 transition-all group"
            >
              <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                <Phone className="w-7 h-7 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Teléfono</p>
                <p className="text-white font-bold">{config.phone || '+34 667 472 074'}</p>
              </div>
            </a>

            <a
              href={`https://wa.me/${config.whatsapp || '34667472074'}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-4 p-8 bg-slate-800/50 hover:bg-slate-800 rounded-3xl border border-slate-700/50 transition-all group"
            >
              <div className="w-16 h-16 rounded-2xl bg-green-500/20 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                <svg className="w-7 h-7 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">WhatsApp</p>
                <p className="text-white font-bold">Escríbeme</p>
              </div>
            </a>

            <a
              href={`mailto:${config.email || 'info@luisherreraeventos.es'}`}
              className="flex flex-col items-center gap-4 p-8 bg-slate-800/50 hover:bg-slate-800 rounded-3xl border border-slate-700/50 transition-all group"
            >
              <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                <Mail className="w-7 h-7 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Email</p>
                <p className="text-white font-bold text-sm">{config.email || 'info@luisherreraeventos.es'}</p>
              </div>
            </a>

            {config.instagram && (
              <a
                href={`https://instagram.com/${config.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-4 p-8 bg-slate-800/50 hover:bg-slate-800 rounded-3xl border border-slate-700/50 transition-all group"
              >
                <div className="w-16 h-16 rounded-2xl bg-pink-500/20 flex items-center justify-center group-hover:bg-pink-500/30 transition-colors">
                  <Instagram className="w-7 h-7 text-pink-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Instagram</p>
                  <p className="text-white font-bold">{config.instagram}</p>
                </div>
              </a>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`¡Mira este portfolio de servicios para eventos!\n\n${window.location.href}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-bold transition-colors shadow-lg"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Compartir
            </a>
            <button
              onClick={async () => {
                try {
                  const response = await fetch(`${API}/api/dossier/pdf/${token}`);
                  if (!response.ok) throw new Error('Error');
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `Dossier_${dossier?.client_name || 'Portfolio'}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  console.error('Error downloading PDF:', e);
                  alert('Error al descargar el PDF');
                }
              }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-slate-700 hover:bg-slate-600 text-white rounded-2xl font-bold transition-colors border border-slate-600 cursor-pointer"
            >
              <Download className="h-5 w-5" />
              Descargar PDF
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} {config.business_name || 'Luis Herrera Eventos'}
          </p>
          {config.website && (
            <a 
              href={config.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm mt-3 transition-colors"
            >
              <Globe className="w-4 h-4" />
              {config.website.replace('https://', '').replace('http://', '')}
            </a>
          )}
        </div>
      </footer>
    </div>
  );
};

export default DossierPublicView;
